import { useState } from "react";
import Papa from "papaparse";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Upload, Download, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Validation constants
const MAX_ROWS = 500;
const MAX_NAME_LENGTH = 100;
const MAX_COLOR_LENGTH = 50;
const MAX_TYPE_LENGTH = 100;
const MAX_STEM_LENGTH = 50;
const MAX_DURATION_LENGTH = 50;
const MAX_IMAGE_URL_LENGTH = 500;

interface FlowerData {
  name: string;
  color: string;
  type?: string | null;
  stem_length_range?: string | null;
  duration_range?: string | null;
  image_url?: string | null;
}

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

const validateFlower = (flower: FlowerData, rowIndex: number): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!flower.name || flower.name.trim().length === 0) {
    errors.push({ row: rowIndex, field: "name", message: "Name is required" });
  } else if (flower.name.length > MAX_NAME_LENGTH) {
    errors.push({ row: rowIndex, field: "name", message: `Name exceeds ${MAX_NAME_LENGTH} characters` });
  }

  if (!flower.color || flower.color.trim().length === 0) {
    errors.push({ row: rowIndex, field: "color", message: "Color is required" });
  } else if (flower.color.length > MAX_COLOR_LENGTH) {
    errors.push({ row: rowIndex, field: "color", message: `Color exceeds ${MAX_COLOR_LENGTH} characters` });
  }

  if (flower.type && flower.type.length > MAX_TYPE_LENGTH) {
    errors.push({ row: rowIndex, field: "type", message: `Type exceeds ${MAX_TYPE_LENGTH} characters` });
  }

  if (flower.stem_length_range && flower.stem_length_range.length > MAX_STEM_LENGTH) {
    errors.push({ row: rowIndex, field: "stem_length_range", message: `Stem length exceeds ${MAX_STEM_LENGTH} characters` });
  }

  if (flower.duration_range && flower.duration_range.length > MAX_DURATION_LENGTH) {
    errors.push({ row: rowIndex, field: "duration_range", message: `Duration exceeds ${MAX_DURATION_LENGTH} characters` });
  }

  if (flower.image_url && flower.image_url.length > MAX_IMAGE_URL_LENGTH) {
    errors.push({ row: rowIndex, field: "image_url", message: `Image URL exceeds ${MAX_IMAGE_URL_LENGTH} characters` });
  }

  // Validate image URL format if provided
  if (flower.image_url && flower.image_url.trim().length > 0) {
    try {
      new URL(flower.image_url);
    } catch {
      errors.push({ row: rowIndex, field: "image_url", message: "Invalid image URL format" });
    }
  }

  return errors;
};

const sanitizeString = (value: string | undefined): string => {
  if (!value) return "";
  return value.trim().replace(/[\x00-\x1F\x7F]/g, ""); // Remove control characters
};

export const CSVImporter = () => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const { toast } = useToast();

  const parseCSVRow = (row: Record<string, string>): FlowerData => {
    const flower: FlowerData = {
      name: "",
      color: "",
    };

    Object.entries(row).forEach(([header, value]) => {
      const normalizedHeader = header.toLowerCase().trim();
      const sanitizedValue = sanitizeString(value);

      switch (normalizedHeader) {
        case "name":
          flower.name = sanitizedValue;
          break;
        case "color":
          flower.color = sanitizedValue;
          break;
        case "type":
          flower.type = sanitizedValue || null;
          break;
        case "stem length":
        case "stem_length":
        case "stem_length_range":
          flower.stem_length_range = sanitizedValue || null;
          break;
        case "duration":
        case "duration_range":
          flower.duration_range = sanitizedValue || null;
          break;
        case "image":
        case "image_url":
          flower.image_url = sanitizedValue || null;
          break;
      }
    });

    return flower;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    setValidationErrors([]);
    
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith(".csv") && selectedFile.type !== "text/csv") {
      toast({
        title: "Invalid File",
        description: "Please select a CSV file",
        variant: "destructive",
      });
      return;
    }

    // Check file size (max 1MB for CSV)
    if (selectedFile.size > 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "CSV file must be less than 1MB",
        variant: "destructive",
      });
      return;
    }

    setFile(selectedFile);
    previewCSV(selectedFile);
  };

  const previewCSV = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      preview: 5,
      complete: (results) => {
        if (results.errors.length > 0) {
          toast({
            title: "CSV Parse Error",
            description: results.errors[0].message,
            variant: "destructive",
          });
          return;
        }

        const previewData = results.data.map((row: any) => parseCSVRow(row));
        setPreview(previewData);
      },
      error: (error) => {
        toast({
          title: "CSV Parse Error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setValidationErrors([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          if (results.errors.length > 0) {
            throw new Error(`CSV parsing error: ${results.errors[0].message}`);
          }

          // Check row count limit
          if (results.data.length > MAX_ROWS) {
            throw new Error(`Too many rows. Maximum ${MAX_ROWS} rows allowed. Found ${results.data.length} rows.`);
          }

          if (results.data.length === 0) {
            throw new Error("No data found in CSV file");
          }

          // Parse and validate all rows
          const allErrors: ValidationError[] = [];
          const flowers: FlowerData[] = [];

          results.data.forEach((row: any, index: number) => {
            const flower = parseCSVRow(row);
            const rowErrors = validateFlower(flower, index + 2); // +2 for 1-based index and header row
            
            if (rowErrors.length > 0) {
              allErrors.push(...rowErrors);
            } else if (flower.name && flower.color) {
              flowers.push(flower);
            }
          });

          // If there are validation errors, show them and abort
          if (allErrors.length > 0) {
            setValidationErrors(allErrors.slice(0, 10)); // Show first 10 errors
            throw new Error(`Validation failed: ${allErrors.length} error(s) found. Fix the issues and try again.`);
          }

          if (flowers.length === 0) {
            throw new Error("No valid flower data found in CSV. Name and Color are required fields.");
          }

          const { error } = await supabase.from("flowers").insert(flowers);

          if (error) throw error;

          toast({
            title: "Success",
            description: `Imported ${flowers.length} flowers successfully`,
          });

          setFile(null);
          setPreview([]);
          setValidationErrors([]);
          
          const fileInput = document.getElementById("csv-file") as HTMLInputElement;
          if (fileInput) fileInput.value = "";
        } catch (error) {
          console.error("Error importing CSV:", error);
          toast({
            title: "Import Failed",
            description: error instanceof Error ? error.message : "Failed to import CSV",
            variant: "destructive",
          });
        } finally {
          setImporting(false);
        }
      },
      error: (error) => {
        toast({
          title: "CSV Parse Error",
          description: error.message,
          variant: "destructive",
        });
        setImporting(false);
      },
    });
  };

  const downloadTemplate = () => {
    const csvContent =
      "Name,Color,Type,Stem Length,Duration,Image\nRed Rose,Red,Rose,50 - 80 cm,10 - 14 days,https://example.com/red-rose.jpg\nWhite Tulip,White,Tulip,40 - 60 cm,7 - 10 days,https://example.com/white-tulip.jpg\nPink Carnation,Pink,Carnation,60 - 80 cm,12 - 16 days,https://example.com/pink-carnation.jpg";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "flower-template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Import Flowers from CSV</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Upload a CSV file with columns: Name, Color, Type, Stem Length, Duration, Image.
              Name and Color are required fields. Maximum {MAX_ROWS} rows per import.
            </AlertDescription>
          </Alert>

          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => document.getElementById("csv-file")?.click()}
                className="w-full"
              >
                <Upload className="h-4 w-4 mr-2" />
                {file ? file.name : "Select CSV File"}
              </Button>
            </div>

            {validationErrors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium mb-2">Validation Errors:</p>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {validationErrors.map((err, index) => (
                      <li key={index}>
                        Row {err.row}, {err.field}: {err.message}
                      </li>
                    ))}
                  </ul>
                  {validationErrors.length >= 10 && (
                    <p className="text-sm mt-2 italic">Showing first 10 errors...</p>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {preview.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium">Preview (first 5 rows):</h4>
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="p-2 text-left font-medium">Name</th>
                          <th className="p-2 text-left font-medium">Color</th>
                          <th className="p-2 text-left font-medium">Type</th>
                          <th className="p-2 text-left font-medium">Stem Length</th>
                          <th className="p-2 text-left font-medium">Duration</th>
                          <th className="p-2 text-left font-medium">Image</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((row, index) => (
                          <tr key={index} className="border-t">
                            <td className="p-2">{row.name}</td>
                            <td className="p-2">{row.color}</td>
                            <td className="p-2">{row.type || "-"}</td>
                            <td className="p-2">{row.stem_length_range || "-"}</td>
                            <td className="p-2">{row.duration_range || "-"}</td>
                            <td className="p-2 max-w-[200px] truncate">{row.image_url || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {file && (
              <Button onClick={handleImport} disabled={importing} className="w-full">
                {importing ? "Importing..." : "Import Flowers"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
